import { Button, Group, Modal, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { UpdateBrand } from "api/brand.service";
import { safeApiCall } from "api/common.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { useEffect, useState } from "react";
import { Brand } from "types/model.types";

interface BrandModalProps {
  opened: boolean;
  onClose: () => void;
  brand: Brand | null;
  existingBrands: Brand[];
  setBrands: React.Dispatch<React.SetStateAction<Brand[]>>;
  forceReload?: () => void;
}

const BrandModal: React.FC<BrandModalProps> = ({
  opened,
  onClose,
  brand,
  existingBrands,
  setBrands,
  forceReload,
}) => {
  const [name, setName] = useState("");

  useEffect(() => {
    setName(brand?.name ?? "");
  }, [brand]);

  // Handle form submission for adding or editing a brand
  // This function checks for duplicate brand names and either adds or updates the brand
  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const isDuplicate = existingBrands.some(
      (b) =>
        b.name.trim().toLowerCase() === trimmedName.toLowerCase() &&
        b.id !== brand?.id
    );

    if (isDuplicate) {
      notifications.show({
        title: "Brand Management",
        message: "Brand with this name already exists.",
        color: MantineNotificationColor.Warn,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    const payload: Brand = {
      id: brand?.id ?? 0,
      name: trimmedName,
      total: 0, // Assuming total is not needed for add/edit
    };

    try {
      await safeApiCall(UpdateBrand(payload));

      notifications.show({
        title: "Brand Saved",
        message: `Brand "${payload.name}" saved successfully.`,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
      forceReload?.();
      onClose();
    } catch (error) {
      console.error("Error saving brand:", error);
      notifications.show({
        title: "Brand Management",
        message: "Something went wrong. Please try again.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={brand?.id ? "Edit Brand" : "Add Brand"}
      centered
    >
      <TextInput
        label="Brand Name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        placeholder="Enter brand name"
        mb="md"
        autoFocus
      />
      <Group justify="flex-end">
        <Button onClick={handleSubmit}>{brand?.id ? "Update" : "Add"}</Button>
      </Group>
    </Modal>
  );
};

export default BrandModal;
